import * as React from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import { Location } from 'types/location.type';
import { useSelector } from 'react-redux';
import {
    getSovLocations,
    getSubmissionDetails,
    getSovLoading,
    getTopLevelUserGroup,
} from 'application/redux/selectors';
import { RouteComponentProps } from 'react-router';
import LumenLoader from 'components/Loader';
import LrStreetView from 'components/LrStreetView';
import { Spacing } from 'components/Layout';
import { formatBoolToYesNo, formatDollar } from 'helpers/formatters';
import { getLocationOptionsData } from 'application/redux/selectors/locationOptions';
import MapIcon from '@mui/icons-material/Map';
import { IconButton, Divider } from '@mui/material';
import LocationDetailDialog from 'components/LrProducerDashboard/components/LocationMap/components/LocationDetailDialog';
import { MapLocation } from 'types/location.type';
import LocationScratchPad from './components/LocationScratchPad';
import { WithStyles } from '@mui/styles';
import styles from './styles';
import { getIsLumenRole } from '../../../../helpers/roles';
import LocationInspectionList from './components/LocationInspectionList';
import { BetterViewSection } from './components/BetterViewSection';
import GoogleResearchSection from './components/GoogleResearchSection';
import { useActions } from '../../../../hooks/useActions';
import * as Actions from '../../../../application/redux/actions';
import { LabeledData } from 'components/Input';
import UnderwritingTrendTableBr from '../../../../components/Underwriting/components/UnderwritingTrendTableBr';
import LocationAccumulationSummaryTable from '../../../../components/Underwriting/components/LocationAccumulationSummaryTable';
import ActivatedTriggerTable from '../../../../components/Underwriting/components/ActivatedTriggerTable';
import { useEdisonDock } from '../../../../hooks/EdisonChatDock';
import { useEffect } from 'react';
import { AiContextTypes } from '../../../../types/ai.types';

// Placeholder table components
const BRZoneUWHistoryTable: React.FC<{ submissionId: string; locationId: number }> = ({ submissionId, locationId }) => {
    return <UnderwritingTrendTableBr submissionId={submissionId} locationId={locationId} />;
};

const BRSOVFieldsSection: React.FC<{ location: Partial<Location>; locationOptions: any; submission: any }> = ({
    location,
    locationOptions,
    submission,
}) => {
    // Helper functions for formatting
    const renderValue = (val: any) => (val != null && val !== '' ? val : 'N/A');
    const boolOrN = (val: any) => (val != null ? formatBoolToYesNo(val) : 'N/A');
    const moneyOrN = (val: any) => (val != null ? formatDollar(val, 0) : 'N/A');
    const stringOrN = (val: any) => (val != null ? String(val) : 'N/A');

    // Create a typed reference to avoid repetitive type assertions
    const loc = location as any;

    // Define groups for the SOV data with render functions
    const groups: { title: string; fields: { key: string; label: string; render: () => any }[] }[] = [
        {
            title: 'Location Information',
            fields: [
                {
                    key: 'effectiveDate',
                    label: 'Effective Date',
                    render: () =>
                        renderValue(submission?.effectiveDate || loc.originalEffectiveDate || loc.effectiveDate),
                },
                { key: 'address1', label: 'Address', render: () => renderValue(location.address1) },
                { key: 'address2', label: 'Address 2', render: () => renderValue(location.address2) },
                { key: 'city', label: 'City', render: () => renderValue(location.city) },
                { key: 'state', label: 'State', render: () => renderValue(location.state) },
                { key: 'zip', label: 'Zip', render: () => renderValue(location.zip) },
                {
                    key: 'county',
                    label: 'County',
                    render: () => renderValue(location.county || location.locationCounty),
                },
                { key: 'locationLat', label: 'Latitude', render: () => stringOrN(location.locationLat) },
                { key: 'locationLon', label: 'Longitude', render: () => stringOrN(location.locationLon) },
            ],
        },
        {
            title: 'Construction & Property',
            fields: [
                {
                    key: 'constructionType',
                    label: 'Construction Type',
                    render: () =>
                        location.constructionType
                            ? locationOptions.LOCATION_CONSTRUCTION_TYPES[location.constructionType] || 'N/A'
                            : 'N/A',
                },
                {
                    key: 'numberOfBuildings',
                    label: '# Buildings',
                    render: () => renderValue(location.numberOfBuildings),
                },
                {
                    key: 'minimumSpaceSeparationFeet',
                    label: 'Min Separation (ft)',
                    render: () => renderValue(location.minimumSpaceSeparationFeet),
                },
                {
                    key: 'hasExternalCombustibleExposuresLt100ft',
                    label: 'Ext. Comb. Exposure < 100ft',
                    render: () => boolOrN(location.hasExternalCombustibleExposuresLt100ft),
                },
            ],
        },
        {
            title: 'Security Features',
            fields: [
                {
                    key: 'isFencedType',
                    label: 'Is Fenced',
                    render: () =>
                        loc.isFencedType ? locationOptions.LOCATION_IS_FENCED_TYPES[loc.isFencedType] || 'N/A' : 'N/A',
                },
                {
                    key: 'fenceHeightType',
                    label: 'Fence Height',
                    render: () =>
                        loc.fenceHeightType
                            ? locationOptions.LOCATION_FENCE_HEIGHT_TYPES[loc.fenceHeightType] || 'N/A'
                            : 'N/A',
                },
                { key: 'isGuarded', label: 'Guard', render: () => boolOrN(location.isGuarded) },
                {
                    key: 'hasElectronicIntrusionDetection',
                    label: 'Elec. Intrusion Det.',
                    render: () => boolOrN(location.hasElectronicIntrusionDetection),
                },
                {
                    key: 'electronicIntrusionDetectionVendorName',
                    label: 'Elec. Intrusion Det. Vendor',
                    render: () => renderValue(location.electronicIntrusionDetectionVendorName),
                },
                {
                    key: 'isElectronicIntrusionDetectionMonitored',
                    label: 'Elec. Intrusion Central Station',
                    render: () => boolOrN(location.isElectronicIntrusionDetectionMonitored),
                },
            ],
        },
        {
            title: 'Water Flow Detection',
            fields: [
                {
                    key: 'hasWaterFlowDetection',
                    label: 'Water Flow Det.',
                    render: () => boolOrN(location.hasWaterFlowDetection),
                },
                {
                    key: 'isWaterFlowCentralStationMonitored',
                    label: 'Central Station',
                    render: () => boolOrN(location.isWaterFlowCentralStationMonitored),
                },
                {
                    key: 'hasWaterFlowAutoRemoteShutOff',
                    label: 'Auto Shutoff',
                    render: () => boolOrN(location.hasWaterFlowAutoRemoteShutOff),
                },
            ],
        },
        {
            title: 'Financial Information',
            fields: [
                { key: 'hardCosts', label: 'Hard Costs', render: () => moneyOrN(location.hardCosts) },
                { key: 'softCosts', label: 'Soft Costs', render: () => moneyOrN(location.softCosts) },
                {
                    key: 'lossOfGrossEarnings',
                    label: 'Loss of Gross Earnings',
                    render: () => moneyOrN(location.lossOfGrossEarnings),
                },
                {
                    key: 'lossOfRentalIncome',
                    label: 'Loss of Rental Income',
                    render: () => moneyOrN(location.lossOfRentalIncome),
                },
                {
                    key: 'totalInsurableValue',
                    label: 'Total Contract Value',
                    render: () => moneyOrN(location.totalInsurableValue),
                },
                { key: 'markedAsDeleted', label: 'Removed', render: () => boolOrN(location.markedAsDeleted) },
            ],
        },
    ];

    return (
        <Grid container justifyContent="space-between" maxWidth="60rem" columnSpacing="6rem">
            {groups.map((group) => (
                <React.Fragment key={group.title}>
                    <Grid xs={12} style={{ marginTop: '1.5rem' }}>
                        <Typography variant="h4">{group.title}</Typography>
                    </Grid>

                    {group.fields.map((field) => {
                        return (
                            <Grid key={field.key} sm={5} style={{ minWidth: '26rem', maxWidth: '40rem' }}>
                                <Grid container direction="row" justifyContent="space-between">
                                    <Typography variant="body2">{field.label}</Typography>
                                    <Typography variant="body2">{field.render()}</Typography>
                                </Grid>
                            </Grid>
                        );
                    })}
                </React.Fragment>
            ))}
        </Grid>
    );
};

const AddressBR: React.FunctionComponent<
    RouteComponentProps<{ id: string; locationId: string }> & WithStyles<typeof styles>
> = (props) => {
    const { classes, match } = props;
    const { locationId } = match.params;
    const { fetchSovReport } = useActions(Actions);

    // Global State
    const submission = useSelector(getSubmissionDetails);
    const sovLocations = useSelector(getSovLocations);
    const sovIsLoading = useSelector(getSovLoading);
    const locationOptions = useSelector(getLocationOptionsData);
    const group = useSelector(getTopLevelUserGroup);
    const isLumenRole = getIsLumenRole(group);

    // Local State
    const location = sovLocations.find((l) => l.id.toString() === locationId) || ({} as Partial<Location>);
    const [openDetailDialog, setOpenDetailDialog] = React.useState<boolean>(false);

    const { setShowResearchTab, setEntityContext, clearEntityContext } = useEdisonDock();

    useEffect(() => {
        setEntityContext(AiContextTypes.LOCATION, Number(locationId));
        setShowResearchTab(true);
        return () => {
            clearEntityContext();
            setShowResearchTab(false);
        };
    }, []);

    if (!submission || sovIsLoading) return <LumenLoader />;

    const title = `${location.submissionLocationNumber ? location.submissionLocationNumber + ') ' : ''}${
        location.locationName
    } Location Review${location.totalInsurableValue ? ` (${formatDollar(location.totalInsurableValue, 0)} TIV)` : ''}`;

    const getMapLocation: () => MapLocation = () => {
        const ret: MapLocation = {} as MapLocation;
        ret.TIV = location.totalInsurableValue;
        ret.locationName = location.locationName;
        ret.formattedAddress = location.formattedAddress;
        ret.locationLat = location.locationLat;
        ret.locationLon = location.locationLon;
        ret.locationId = location.id ? String(location.id) : undefined;
        ret.buildingValue = location.buildingValue;
        ret.biRentalIncomeValue = location.biRentalIncomeValue;
        ret.personalPropertyValue = location.personalPropertyValue;
        ret.otherValue = location.otherValue;
        ret.accountName = submission.namedInsured;
        return ret;
    };

    return (
        <>
            <Grid
                container
                className={classes.root}
                direction="column"
                justifyContent="center"
                alignItems="stretch"
                spacing={0}
            >
                <Grid>
                    <Typography variant={'h2'}>
                        {title}
                        <IconButton onClick={() => setOpenDetailDialog(!openDetailDialog)} size="large">
                            <MapIcon color="primary" />
                        </IconButton>
                    </Typography>
                    <div style={{ marginLeft: '0.75rem' }}>
                        <Typography
                            variant={'subtitle1'}
                        >{`${location.formattedAddress} - ${location.locationCounty}`}</Typography>
                    </div>
                </Grid>

                <Grid container xs={11} direction="column">
                    <Grid className={classes.margin} style={{ width: '37.5rem' }}>
                        <Divider sx={{ my: '1rem', opacity: 0.9 }} />
                        <Typography className={classes.headerLabel}>Accumulations</Typography>
                        <LocationAccumulationSummaryTable
                            submissionId={submission.id}
                            locationIds={[Number(locationId)]}
                            tivLabel={'Exposure'}
                            showPercentOfBound={false}
                        />
                        <Divider sx={{ my: '1rem', opacity: 0.9 }} />
                        <Typography className={classes.headerLabel}>Zone UW History</Typography>
                        <BRZoneUWHistoryTable submissionId={submission.id} locationId={Number(locationId)} />
                        <Divider sx={{ my: '1rem', opacity: 0.9 }} />
                        {isLumenRole && (
                            <>
                                <Typography className={classes.headerLabel}>Triggers</Typography>
                                <ActivatedTriggerTable locationId={Number(locationId)} />
                                <Divider sx={{ my: '1rem', opacity: 0.9 }} />
                            </>
                        )}
                    </Grid>
                    <Grid className={classes.margin} style={{ width: '100%' }}>
                        <Typography className={classes.headerLabel}>SOV Information</Typography>
                        <BRSOVFieldsSection
                            location={location}
                            locationOptions={locationOptions}
                            submission={submission}
                        />
                    </Grid>
                </Grid>

                {isLumenRole && (
                    <BetterViewSection
                        location={location}
                        submissionId={submission.id}
                        classes={classes}
                        onRefresh={() => fetchSovReport(submission.id)}
                    />
                )}

                <LrStreetView location={location} />

                <GoogleResearchSection location={location} classes={classes} searchTerms={[{ term: 'fire' }]} />

                <Spacing height={'1.5rem'} />
                <Grid className={classes.margin} style={{ width: '37.5rem' }}>
                    <Typography className={classes.headerLabel}>Inspections</Typography>
                    <LocationInspectionList
                        locationId={locationId}
                        locationName={location.locationName || ''}
                        locationAddress={location.formattedAddress}
                    />
                </Grid>
            </Grid>

            {!!location && (
                <>
                    <LocationScratchPad
                        submission={submission}
                        locationId={locationId}
                        initialNotes={location.underwritingNote}
                    />
                    <LocationDetailDialog
                        open={openDetailDialog}
                        onClose={() => setOpenDetailDialog(false)}
                        locationData={getMapLocation()}
                    />
                </>
            )}
        </>
    );
};

export default AddressBR;
